import request from '@/config/axios'
import { asCapacityPage, type CapacityPageResult } from '@/api/capacity/page'

export interface RiskReview {
  reviewId: string
  phone: string
  waybillId: string
  reason?: string
  status: string
  createdAt?: string
}

export interface PenaltyRecord {
  penaltyId: string
  reviewId?: string
  phone: string
  waybillId?: string
  level: string
  amount?: number
  debited?: number
  debtAdded?: number
  days?: number
  limitUntil?: string
  actorId?: string
  actorRole?: string
  reason?: string
  active: boolean
  createdAt?: string
}

export interface AppealRecord {
  appealId: string
  penaltyId: string
  phone: string
  reason?: string
  status: string
  decisionNote?: string
  createdAt?: string
}

export interface PenaltyResult {
  success: boolean
  message?: string
  reviewId?: string
  penaltyId?: string
  appealId?: string
}

export const listReviews = async (params?: {
  openOnly?: boolean
  pageNo?: number
  pageSize?: number
}): Promise<CapacityPageResult<RiskReview>> => {
  const raw = await request.get({ url: '/capacity/penalty/reviews', params })
  return asCapacityPage<RiskReview>(raw, params?.pageNo, params?.pageSize)
}

export const openReview = (phone: string, waybillId: string, reason?: string) =>
  request.post<PenaltyResult>({
    url: '/capacity/penalty/reviews/open',
    params: { phone, waybillId, reason }
  })

export const dismissReview = (reviewId: string, note?: string) =>
  request.post<PenaltyResult>({
    url: '/capacity/penalty/reviews/dismiss',
    params: { reviewId, note }
  })

export const revokePenalty = (penaltyId: string, actorId: string, note?: string) =>
  request.post<PenaltyResult>({
    url: '/capacity/penalty/revoke',
    params: { penaltyId, actorId, note }
  })

export const applyPenalty = (data: {
  actorId: string
  actorRole: string
  reviewId: string
  level: string
  amount?: number
  days?: number
  reason?: string
}) =>
  request.post<PenaltyResult>({
    url: '/capacity/penalty/apply',
    params: data
  })

export const listPenalties = async (params?: {
  phone?: string
  pageNo?: number
  pageSize?: number
}): Promise<CapacityPageResult<PenaltyRecord>> => {
  const raw = await request.get({ url: '/capacity/penalty/list', params })
  return asCapacityPage<PenaltyRecord>(raw, params?.pageNo, params?.pageSize)
}

export const listAppeals = async (params?: {
  pendingOnly?: boolean
  pageNo?: number
  pageSize?: number
}): Promise<CapacityPageResult<AppealRecord>> => {
  const raw = await request.get({ url: '/capacity/penalty/appeals', params })
  return asCapacityPage<AppealRecord>(raw, params?.pageNo, params?.pageSize)
}

export const submitAppeal = (phone: string, penaltyId: string, reason?: string) =>
  request.post<PenaltyResult>({
    url: '/capacity/penalty/appeals/submit',
    params: { phone, penaltyId, reason }
  })

export const decideAppeal = (appealId: string, approve: boolean, note?: string) =>
  request.post<PenaltyResult>({
    url: '/capacity/penalty/appeals/decide',
    params: { appealId, approve, note }
  })

export const listMessages = (phone: string) =>
  request.get<string[]>({ url: '/capacity/penalty/messages', params: { phone } })

export const getBlock = (phone: string) =>
  request.get<{ code?: string; message?: string }>({
    url: '/capacity/penalty/block',
    params: { phone }
  })
